import type { AxiosError } from 'axios'

export const CAPTCHA_REQUIRED_CODE = 'CAPTCHA_REQUIRED' as const
export const CAPTCHA_VERIFICATION_FAILED_CODE =
  'CAPTCHA_VERIFICATION_FAILED' as const
export const CAPTCHA_TOKEN_HEADER = 'X-Captcha-Token' as const

export const CAPTCHA_PROVIDERS = ['turnstile'] as const
export type CaptchaProvider = (typeof CAPTCHA_PROVIDERS)[number]

// `invisible` is a known wire value without a presenter yet; it resolves to
// `unsupported_mode`.
export const CAPTCHA_MODES = ['managed', 'invisible'] as const
export type CaptchaMode = (typeof CAPTCHA_MODES)[number]

export interface CaptchaChallenge {
  provider: CaptchaProvider
  mode: CaptchaMode
  siteKey: string
}

export interface CaptchaProof {
  token: string
}

export const CAPTCHA_FAILURE_CODES = {
  repeatedChallenge: 'repeated_challenge',
  presentationUnavailable: 'presentation_unavailable',
  missingSiteKey: 'missing_site_key',
  malformedChallenge: 'malformed_challenge',
  verificationRejected: 'verification_rejected',
} as const

export type CaptchaSolveResult =
  | { outcome: 'solved'; proof: CaptchaProof }
  | { outcome: 'cancelled' }
  | { outcome: 'failed'; code: string }
  | { outcome: 'unsupported_mode'; mode: CaptchaMode }

export type CaptchaUnsolvedResult = Exclude<
  CaptchaSolveResult,
  { outcome: 'solved' }
>

/**
 * Receives each widget-level failure as it happens, even when the user then
 * retries or closes.
 */
export type CaptchaWidgetErrorReporter = (errorCode: string) => void

/**
 * Turnstile metadata for the protected request; cData must match that
 * request's identifier.
 */
export interface CaptchaSolveOptions {
  onWidgetError: CaptchaWidgetErrorReporter
  /** Identifies the protected operation to Turnstile. */
  action?: string
  /** Request-specific data for Turnstile, such as the OTP destination. */
  cData?: string
  /**
   * Open on the failure state: the backend refused the previous token, so the
   * user retries explicitly.
   */
  afterRejection?: boolean
}

/** Never rejects: every terminal state is a `CaptchaSolveResult`. */
export type CaptchaSolver = (
  challenge: CaptchaChallenge,
  options: CaptchaSolveOptions,
) => Promise<CaptchaSolveResult>

export class CaptchaUnsolvedError extends Error {
  readonly result: CaptchaUnsolvedResult

  constructor(result: CaptchaUnsolvedResult) {
    super(`CAPTCHA not solved: ${result.outcome}`)
    this.name = 'CaptchaUnsolvedError'
    this.result = result
    Object.setPrototypeOf(this, CaptchaUnsolvedError.prototype)
  }
}

/**
 * A cancelled challenge is user intent (close, navigation, superseded
 * attempt): no message.
 */
export function isCaptchaCancellation(error: unknown): boolean {
  return (
    error instanceof CaptchaUnsolvedError &&
    error.result.outcome === 'cancelled'
  )
}

/**
 * A challenge that ended without a token for a reason the user should be
 * told about.
 */
export function isCaptchaFailure(error: unknown): boolean {
  return (
    error instanceof CaptchaUnsolvedError &&
    error.result.outcome !== 'cancelled'
  )
}

/** Identifies the protected operation in telemetry; one per opted-in endpoint. */
export const CAPTCHA_OPERATIONS = {
  guestPhoneOtp: 'guest_phone_otp',
} as const
export type CaptchaOperation =
  (typeof CAPTCHA_OPERATIONS)[keyof typeof CAPTCHA_OPERATIONS]

export type CaptchaRequestHeaders = Record<string, string>

export type CaptchaRequest<T> = (
  headers?: CaptchaRequestHeaders,
) => Promise<T>

export interface CaptchaSettings {
  /** Solve before the first request instead of waiting for a 403 challenge. */
  proactiveEnabled: boolean
  /** Site key for proactive solves, and for challenges that omit their own. */
  siteKey: string
}

export const CAPTCHA_EVENTS = {
  required: 'captcha_required',
  opened: 'captcha_opened',
  solved: 'captcha_solved',
  cancelled: 'captcha_cancelled',
  clientError: 'captcha_client_error',
  resubmitSuccess: 'captcha_resubmit_success',
  resubmitFailure: 'captcha_resubmit_failure',
} as const
export type CaptchaEventName =
  (typeof CAPTCHA_EVENTS)[keyof typeof CAPTCHA_EVENTS]

/** Where in the lifecycle a Sentry-worthy failure happened. */
export type CaptchaTelemetryPhase =
  | 'parse'
  | 'render'
  | 'solve'
  | 'resubmit'

export interface CaptchaTelemetryEvent {
  name: CaptchaEventName
  operation: CaptchaOperation
  challenge?: CaptchaChallenge
  result?: CaptchaSolveResult
  errorCode?: string
  elapsedMs?: number
}

export interface CaptchaIssue {
  operation: string
  phase: CaptchaTelemetryPhase
  errorCode: string
  challenge?: CaptchaChallenge
}

/**
 * Telemetry must never change request behavior: implementations swallow
 * their own failures.
 */
export interface CaptchaTelemetry {
  track: (event: CaptchaTelemetryEvent) => void
  reportUnexpected: (issue: CaptchaIssue) => void
}

export interface CaptchaCoordinatorOptions<T> {
  operation: CaptchaOperation
  /**
   * Runs again with the proof header after each solved challenge, so every
   * run must carry the same logical request.
   */
  request: CaptchaRequest<T>
  solve: CaptchaSolver
  settings: CaptchaSettings
  action?: string
  cData?: string
  telemetry?: CaptchaTelemetry
  isCurrent?: () => boolean
}

export type CaptchaAxiosError = AxiosError<unknown>
